import { useCallback } from 'react';
import { z } from 'zod';
import { useNotification } from '@/hooks';
import { NotificationSeverity } from '@/components/notifications/Notification';
import { getErrorMessage } from '@/utils';
import { getScopeErrorMessage } from '@/pages/Business/pages/Monitoring/utils/getScopeErrorMessage';
import { isValidUUID } from '@/pages/Business/pages/Commerce/utils';
import { useAddShippingsToRouteMutation } from '@/pages/Business/pages/Monitoring/services/routes.service';
import {
  useGetShippingsForRoutingMutation,
  useGenerateRouteMutation,
  useEnrichCustomRouteMutation,
  useCreateRouteMutation,
  useUpdateRouteMutation,
  useUpdateVisitCoordinatesMutation,
  useUpdateVisitOrderMutation,
  useFindShippingByNumberMutation,
  useFindPendingShippingMutation,
  useFindShippingsByPolygonMutation,
  useRemoveShippingsFromRouteMutation,
  useCreatePolygonMutation,
  useUpdateZoneMutation,
  useDeleteZoneMutation,
} from '../../../services';
import {
  RoutePoint,
  ShippingForRouting,
  CreateRouteRequest,
  ShippingInputDto,
  GenerateRouteRequest,
  Zone,
  RemoveShippingsFromRouteResponse,
} from '../../../models';
import { getAddressForShipping } from '../../../utils/shippingAddress';
import { MAX_SHIPPINGS_ON_MAP } from '../../../config';
import { haversineDistanceMeters } from '../../../utils/haversineDistanceMeters';
import { sortRoutePointsByServerOrder } from '../utils/sortRoutePointsByServerOrder';
import { canDeleteSavedPolygon } from '../utils/can-delete-saved-polygon';

const requireOperator = (
  operatorId: string,
  notify: (msg: string, severity: NotificationSeverity) => void,
  message = 'El operador logístico es requerido.'
): boolean => {
  if (!operatorId?.trim()) {
    notify(message, NotificationSeverity.error);
    return false;
  }
  return true;
};

const shippingNumbersForRouteAddSchema = z
  .array(
    z.coerce
      .number({
        invalid_type_error: 'Cada número de pedido debe ser un número válido.',
      })
      .int('Cada número de pedido debe ser un entero.')
      .positive('Cada número de pedido debe ser positivo.')
  )
  .min(1, 'Debe incluir al menos un pedido.');

export const useRouteBuilderActions = (logisticOperatorId: string) => {
  const { showNotification } = useNotification();
  const [getShippingsForRouting] = useGetShippingsForRoutingMutation();
  const [findShippingByNumber] = useFindShippingByNumberMutation();
  const [findPendingShipping] = useFindPendingShippingMutation();
  const [findShippingsByPolygon] = useFindShippingsByPolygonMutation();
  const [addShippingsToRoute] = useAddShippingsToRouteMutation();
  const [removeShippingsFromRouteMutation] =
    useRemoveShippingsFromRouteMutation();
  const [generateRoute] = useGenerateRouteMutation();
  const [enrichCustomRoute] = useEnrichCustomRouteMutation();
  const [createRoute] = useCreateRouteMutation();
  const [updateRoute] = useUpdateRouteMutation();
  const [updateVisitCoordinates] = useUpdateVisitCoordinatesMutation();
  const [updateVisitOrder] = useUpdateVisitOrderMutation();
  const [createPolygon] = useCreatePolygonMutation();
  const [updateZone] = useUpdateZoneMutation();
  const [deleteZone, { isLoading: isDeletingSavedPolygon }] =
    useDeleteZoneMutation();

  const loadShippings = useCallback(
    async (numbers: number[]): Promise<ShippingForRouting[] | null> => {
      if (
        !requireOperator(
          logisticOperatorId,
          showNotification,
          'El operador logístico es requerido para rutear.'
        )
      )
        return null;

      if (numbers.length > MAX_SHIPPINGS_ON_MAP) {
        showNotification(
          `No se pueden cargar más de ${MAX_SHIPPINGS_ON_MAP} pedidos en el mapa`,
          NotificationSeverity.error
        );
        return null;
      }

      try {
        const result = await getShippingsForRouting({
          numbers,
          logisticOperatorId,
        }).unwrap();

        if (result.shippings && result.shippings.length > 0) {
          if (result.shippings.length < numbers.length) {
            showNotification(
              `Se cargaron ${result.shippings.length} de ${numbers.length} envíos. Los demás no están en un estado que permita rutearlos.`,
              NotificationSeverity.warning
            );
          } else {
            showNotification(
              `${result.shippings.length} envío(s) cargado(s) correctamente`,
              NotificationSeverity.success
            );
          }
          return result.shippings;
        } else {
          showNotification(
            'Ninguno de los envíos seleccionados se puede rutear. Verifique que su estado permita rutearlos.',
            NotificationSeverity.warning
          );
          return [];
        }
      } catch (error: any) {
        const errorMessage =
          getScopeErrorMessage(error) ??
          getErrorMessage(error, 'Error al cargar envíos para ruteo');
        showNotification(errorMessage, NotificationSeverity.error);
        return null;
      }
    },
    [logisticOperatorId, getShippingsForRouting, showNotification]
  );

  const searchShipping = useCallback(
    async (search: string): Promise<ShippingForRouting | null> => {
      if (
        !requireOperator(
          logisticOperatorId,
          showNotification,
          'El operador logístico es requerido para buscar pedidos.'
        )
      )
        return null;

      const trimmed = (search ?? '').toString().trim();
      if (!trimmed) {
        showNotification(
          'Ingrese un ID Pedido o ID Externo',
          NotificationSeverity.warning
        );
        return null;
      }

      try {
        const result = await findShippingByNumber({
          search: trimmed,
          logisticOperatorId,
        }).unwrap();
        return result;
      } catch (error: unknown) {
        showNotification(
          getScopeErrorMessage(error) ??
            getErrorMessage(error, 'Error al buscar el pedido'),
          NotificationSeverity.error
        );
        return null;
      }
    },
    [logisticOperatorId, findShippingByNumber, showNotification]
  );

  const addShippingsToRouting = useCallback(
    async (
      routeId: string,
      shippingNumbers: unknown[]
    ): Promise<boolean | null> => {
      if (
        !requireOperator(
          logisticOperatorId,
          showNotification,
          'El operador logístico es requerido.'
        )
      )
        return null;

      const parsed =
        shippingNumbersForRouteAddSchema.safeParse(shippingNumbers);
      if (!parsed.success) {
        const first = parsed.error.issues[0];
        showNotification(
          first?.message ?? 'Los números de pedido no son válidos.',
          NotificationSeverity.error
        );
        return null;
      }

      try {
        await addShippingsToRoute({
          routeId,
          logisticOperatorId,
          shippingNumbers: parsed.data,
        }).unwrap();
        return true;
      } catch (error: unknown) {
        showNotification(
          getScopeErrorMessage(error) ??
            getErrorMessage(error, 'Error al agregar pedidos a la ruta'),
          NotificationSeverity.error
        );
        return null;
      }
    },
    [logisticOperatorId, addShippingsToRoute, showNotification]
  );

  const removeShippingsFromRoute = useCallback(
    async (
      routeId: string,
      shippingNumbers: number[]
    ): Promise<RemoveShippingsFromRouteResponse | null> => {
      if (
        !requireOperator(
          logisticOperatorId,
          showNotification,
          'El operador logístico es requerido.'
        )
      )
        return null;
      if (shippingNumbers.length === 0) return null;

      try {
        return await removeShippingsFromRouteMutation({
          routeId,
          shippingNumbers,
          logisticOperatorId,
        }).unwrap();
      } catch (error: unknown) {
        showNotification(
          getScopeErrorMessage(error) ??
            getErrorMessage(error, 'Error al quitar pedidos de la ruta'),
          NotificationSeverity.error
        );
        return null;
      }
    },
    [logisticOperatorId, removeShippingsFromRouteMutation, showNotification]
  );

  const searchShippingByCode = useCallback(
    async (code: string): Promise<ShippingForRouting | null> => {
      if (
        !requireOperator(
          logisticOperatorId,
          showNotification,
          'El operador logístico es requerido para buscar pedidos.'
        )
      )
        return null;

      const trimmedCode = (code ?? '').toString().trim();
      if (!trimmedCode) {
        showNotification(
          'Ingrese un número o código de pedido.',
          NotificationSeverity.warning
        );
        return null;
      }

      try {
        const result = await findPendingShipping({
          code: trimmedCode,
          logisticOperatorId,
        }).unwrap();
        return result;
      } catch (error: any) {
        showNotification(
          getScopeErrorMessage(error) ??
            getErrorMessage(
              error,
              'No hay envío pendiente con el código ingresado.'
            ),
          NotificationSeverity.error
        );
        return null;
      }
    },
    [logisticOperatorId, findPendingShipping, showNotification]
  );

  const loadShippingsByPolygon = useCallback(
    async (polygon: {
      type: 'Polygon';
      coordinates: number[][][];
    }): Promise<ShippingForRouting[] | null> => {
      if (
        !requireOperator(
          logisticOperatorId,
          showNotification,
          'El operador logístico es requerido para buscar pedidos por polígono.'
        )
      )
        return null;

      try {
        const result = await findShippingsByPolygon({
          polygon,
          logisticOperatorId,
        }).unwrap();

        if (result.shippings && result.shippings.length > 0) {
          const reachedLimit =
            result.shippings.length >= MAX_SHIPPINGS_ON_MAP;
          showNotification(
            reachedLimit
              ? `Se muestran los primeros ${MAX_SHIPPINGS_ON_MAP} pedidos del polígono. Achicá el área para ver el resto.`
              : `${result.shippings.length} pedido(s) encontrado(s) dentro del polígono`,
            reachedLimit
              ? NotificationSeverity.warning
              : NotificationSeverity.success
          );
          return result.shippings;
        } else {
          showNotification(
            'No se encontraron pedidos pendientes dentro del polígono seleccionado.',
            NotificationSeverity.warning
          );
          return [];
        }
      } catch (error: any) {
        const errorMessage =
          getScopeErrorMessage(error) ??
          getErrorMessage(error, 'Error al buscar pedidos por polígono');
        showNotification(errorMessage, NotificationSeverity.error);
        return null;
      }
    },
    [logisticOperatorId, findShippingsByPolygon, showNotification]
  );

  const savePolygon = useCallback(
    async (
      name: string,
      geojson: unknown,
      color?: string
    ): Promise<Zone | null> => {
      if (
        !requireOperator(
          logisticOperatorId,
          showNotification,
          'El operador logístico es requerido para guardar polígonos.'
        )
      )
        return null;

      try {
        const created = await createPolygon({
          name: name.trim(),
          geojson,
          logisticOperatorId,
          color,
        }).unwrap();
        showNotification(
          'Polígono guardado correctamente',
          NotificationSeverity.success
        );
        return { ...created, isPolygon: true };
      } catch (error: unknown) {
        showNotification(
          getScopeErrorMessage(error) ??
            getErrorMessage(error, 'Error al guardar el polígono'),
          NotificationSeverity.error
        );
        return null;
      }
    },
    [logisticOperatorId, createPolygon, showNotification]
  );

  const validateShippingsForRoute = useCallback(
    (shippings: ShippingForRouting[]): boolean => {
      const hasInvalidNumbers = shippings.some(
        (s) => !s.number || s.number.toString().trim() === ''
      );

      if (hasInvalidNumbers) {
        showNotification(
          'Algunos envíos no tienen número válido',
          NotificationSeverity.error
        );
        return false;
      }

      return true;
    },
    [showNotification]
  );

  const validateLogisticOperatorId = useCallback(
    (operatorId: string): boolean => {
      if (!isValidUUID(operatorId)) {
        showNotification(
          'El ID del operador logístico no es válido',
          NotificationSeverity.error
        );
        return false;
      }
      return true;
    },
    [showNotification]
  );

  const transformShippingsToInput = useCallback(
    (
      shippings: ShippingForRouting[],
      baseHint?: { baseLat?: number; baseLong?: number }
    ): ShippingInputDto[] => {
      const baseLatitude = baseHint?.baseLat;
      const baseLongitude = baseHint?.baseLong;
      const hasBase =
        typeof baseLatitude === 'number' &&
        typeof baseLongitude === 'number' &&
        !Number.isNaN(baseLatitude) &&
        !Number.isNaN(baseLongitude);

      let ordered = [...shippings];
      if (hasBase) {
        ordered.sort((firstShipping, secondShipping) => {
          const addressFirst = getAddressForShipping(firstShipping);
          const addressSecond = getAddressForShipping(secondShipping);
          if (!addressFirst || !addressSecond) return 0;
          const distanceMetersBaseToFirstAddress = haversineDistanceMeters(
            { lat: baseLatitude, long: baseLongitude },
            { lat: addressFirst.lat, long: addressFirst.long }
          );
          const distanceMetersBaseToSecondAddress = haversineDistanceMeters(
            { lat: baseLatitude, long: baseLongitude },
            { lat: addressSecond.lat, long: addressSecond.long }
          );
          return (
            distanceMetersBaseToFirstAddress - distanceMetersBaseToSecondAddress
          );
        });
      }

      return ordered.map((shipping, index) => {
        const address = getAddressForShipping(shipping);
        const lat = address ? Number(address.lat) : NaN;
        const long = address ? Number(address.long) : NaN;
        const hasValidCoords = address && !isNaN(lat) && !isNaN(long) && lat !== 0 && long !== 0;

        return {
          number: shipping.number.toString(),
          scanOrder: index,
          logisticOperatorId: shipping.logisticOperatorId ?? logisticOperatorId,
          ...(hasValidCoords && { lat, long }),
        };
      });
    },
    [logisticOperatorId]
  );

  const buildRouteRequest = useCallback(
    (
      shippingsInput: ShippingInputDto[],
      options?: {
        strategy?: string;
        baseLat?: number;
        baseLong?: number;
        polygon?: { type: string; coordinates: number[][][] };
      }
    ): GenerateRouteRequest => {
      return {
        shippings: shippingsInput,
        ...(logisticOperatorId?.trim() && {
          routeLogisticOperatorId: logisticOperatorId.trim(),
        }),
        ...(options?.strategy && { strategy: options.strategy }),
        ...(options?.baseLat !== undefined && { baseLat: options.baseLat }),
        ...(options?.baseLong !== undefined && { baseLong: options.baseLong }),
        ...(options?.polygon && { polygon: options.polygon }),
      };
    },
    [logisticOperatorId]
  );

  const calculateRoute = useCallback(
    async (
      shippings: ShippingForRouting[],
      options?: {
        strategy?: string;
        baseLat?: number;
        baseLong?: number;
        polygon?: { type: string; coordinates: number[][][] };
      }
    ): Promise<{
      route: RoutePoint[];
      totalDistanceMeters?: number;
    } | null> => {
      try {
        if (!validateShippingsForRoute(shippings)) {
          return null;
        }

        if (!validateLogisticOperatorId(logisticOperatorId)) {
          return null;
        }

        const shippingsInput = transformShippingsToInput(shippings, {
          baseLat: options?.baseLat,
          baseLong: options?.baseLong,
        });
        const requestBody = buildRouteRequest(shippingsInput, options);

        const result = await generateRoute(requestBody).unwrap();

        const sortedRoute = sortRoutePointsByServerOrder(result.route);

        const isBasePoint = (p: RoutePoint) =>
          p.tag === 'BASE' || p.tag === 'BASE_POINT';
        const visitCount = sortedRoute.filter(
          (p) => !isBasePoint(p) && (p.shippings?.length ?? 0) > 0
        ).length;

        showNotification(
          `Ruta calculada correctamente con ${visitCount} punto(s)`,
          NotificationSeverity.success
        );

        return {
          route: sortedRoute,
          totalDistanceMeters: result.totalDistanceMeters,
        };
      } catch (error: any) {
        const errorMessage =
          getScopeErrorMessage(error) ??
          getErrorMessage(error, 'Error al calcular la ruta');
        showNotification(errorMessage, NotificationSeverity.error);
        return null;
      }
    },
    [
      logisticOperatorId,
      generateRoute,
      showNotification,
      validateShippingsForRoute,
      validateLogisticOperatorId,
      transformShippingsToInput,
      buildRouteRequest,
    ]
  );

  const enrichRoute = useCallback(
    async (
      points: Array<{
        lat: number;
        long: number;
        tag?: string;
        precedenceGroupId?: string;
        precedenceOrder?: number;
      }>
    ): Promise<{
      route: RoutePoint[];
      totalDistanceMeters?: number;
    } | null> => {
      try {
        const result = await enrichCustomRoute({ points }).unwrap();
        const route = result?.route;
        if (!route || !Array.isArray(route)) return null;

        const mappedRoute = route.map(
          (
            p: RoutePoint & {
              durationFromPrevious?: number;
              distanceFromPrevious?: number;
            },
            index: number
          ) => ({
            ...p,
            order: index,
            travelTime: p.travelTime ?? p.durationFromPrevious ?? 0,
            travelDistanceMeters:
              p.travelDistanceMeters ?? p.distanceFromPrevious ?? undefined,
            distanceMeters:
              p.distanceMeters ?? p.distanceFromPrevious ?? undefined,
          })
        );

        return {
          route: mappedRoute,
          totalDistanceMeters: result.totalDistanceMeters,
        };
      } catch (error: unknown) {
        const errorMessage = getErrorMessage(
          error,
          'Error al enriquecer la ruta'
        );
        showNotification(errorMessage, NotificationSeverity.error);
        return null;
      }
    },
    [enrichCustomRoute, showNotification]
  );

  const saveRoute = useCallback(
    async (routeData: CreateRouteRequest) => {
      const { id: routeId, ...routeDataWithoutId } = routeData;
      const isUpdate = Boolean(routeId);

      try {
        const result = isUpdate
          ? await updateRoute({
              routeId: routeId as string,
              routeData: routeDataWithoutId,
              logisticOperatorId,
            }).unwrap()
          : await createRoute({
              routeData,
              logisticOperatorId,
            }).unwrap();

        showNotification(
          isUpdate
            ? 'Ruta actualizada correctamente'
            : 'Ruta creada correctamente',
          NotificationSeverity.success
        );
        return result;
      } catch (error: any) {
        const errorMessage =
          getScopeErrorMessage(error) ??
          getErrorMessage(
            error,
            isUpdate ? 'Error al actualizar la ruta' : 'Error al crear la ruta'
          );
        showNotification(errorMessage, NotificationSeverity.error);
        throw error;
      }
    },
    [logisticOperatorId, createRoute, updateRoute, showNotification]
  );

  const updateCoordinates = useCallback(
    async (
      routeId: string,
      visitId: string,
      coordinates: {
        lat: number;
        long: number;
        coordinatesChanged: boolean;
        changedByUserId?: string;
      }
    ) => {
      try {
        await updateVisitCoordinates({
          routeId,
          visitId,
          coordinates,
        }).unwrap();
      } catch (error: any) {
        const errorMessage =
          getScopeErrorMessage(error) ??
          getErrorMessage(error, 'Error al actualizar coordenadas');
        showNotification(errorMessage, NotificationSeverity.error);
        throw error;
      }
    },
    [updateVisitCoordinates, showNotification]
  );

  const reorderVisits = useCallback(
    async (routeId: string, visitIds: string[]) => {
      try {
        await updateVisitOrder({ routeId, visitIds }).unwrap();
      } catch (error: unknown) {
        const errorMessage =
          getScopeErrorMessage(error) ??
          getErrorMessage(error, 'Error al reordenar visitas');
        showNotification(errorMessage, NotificationSeverity.error);
        throw error;
      }
    },
    [updateVisitOrder, showNotification]
  );

  const updatePolygon = useCallback(
    async (zoneId: string, geojson: any, operatorId: string): Promise<Zone> => {
      if (
        !requireOperator(
          operatorId,
          showNotification,
          'El operador logístico es requerido para actualizar polígonos.'
        )
      )
        throw new Error('El operador logístico es requerido');

      try {
        const result = await updateZone({
          zoneId,
          geojson,
          logisticOperatorId: operatorId,
        }).unwrap();
        return result;
      } catch (error: any) {
        const errorMessage =
          getScopeErrorMessage(error) ??
          getErrorMessage(error, 'Error al actualizar el polígono');
        showNotification(errorMessage, NotificationSeverity.error);
        throw error;
      }
    },
    [logisticOperatorId, updateZone, showNotification]
  );

  const deleteSavedPolygon = useCallback(
    async (zone: Zone): Promise<boolean> => {
      if (!canDeleteSavedPolygon(zone)) {
        showNotification(
          'Solo se pueden eliminar polígonos guardados desde el mapa',
          NotificationSeverity.error
        );
        return false;
      }

      if (
        !requireOperator(
          logisticOperatorId,
          showNotification,
          'El operador logístico es requerido para eliminar polígonos.'
        )
      )
        return false;

      try {
        await deleteZone({ zoneId: zone.id, logisticOperatorId }).unwrap();
        showNotification(
          'Polígono eliminado correctamente',
          NotificationSeverity.success
        );
        return true;
      } catch (error: unknown) {
        const err = error as {
          status?: number;
          data?: { status?: number; message?: string };
          message?: string;
        };
        const status = err?.status ?? err?.data?.status;

        if (status === 404) {
          showNotification('Polígono no encontrado', NotificationSeverity.error);
          return false;
        }

        if (status === 409) {
          showNotification(
            'No se puede eliminar el polígono porque está en uso',
            NotificationSeverity.error
          );
          return false;
        }

        showNotification(
          getScopeErrorMessage(error) ??
            getErrorMessage(error, 'Error al eliminar el polígono'),
          NotificationSeverity.error
        );
        return false;
      }
    },
    [logisticOperatorId, deleteZone, showNotification]
  );

  return {
    loadShippings,
    loadShippingsByPolygon,
    searchShipping,
    searchShippingByCode,
    addShippingsToRouting,
    removeShippingsFromRoute,
    calculateRoute,
    enrichRoute,
    saveRoute,
    updateCoordinates,
    reorderVisits,
    savePolygon,
    updatePolygon,
    deleteSavedPolygon,
    isDeletingSavedPolygon,
  };
};
